import React, { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import useTenant from "../../../hooks/useTenant";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  TableHead,
  Typography,
  Button,
} from "@mui/material";
import { useDialog } from "../../../contexts/DialogContext";

// CSS to hide the button during print
const PrintButtonContainer = styled("div")`
  @media print {
    display: none;
  }
`;

const TableCell = styled(MuiTableCell)`
  padding-top: 0;
  padding-bottom: 0;
`;

const TableRow = styled(MuiTableRow)`
  height: 42px;

  &:last-child th,
  &:last-child td {
    border-bottom: 0;
  }
`;

function ProformaInvoicePage({ invoiceData }) {
  const { unSetDialog } = useDialog();
  const [logoSrc, setLogoSrc] = useState(null);
  const { getTenantInvoiceLogo, tenantInvoiceLogo } = useTenant();
  const componentRef = useRef();

  const proformaInvoice = invoiceData;

  const fetchTenantInvoiceLogo = async (tenantId) => {
    return await getTenantInvoiceLogo(tenantId);
  };

  useEffect(() => {
    if (tenantInvoiceLogo != null) {
      const imageUrl = URL.createObjectURL(tenantInvoiceLogo);
      setLogoSrc(imageUrl);
    }
  }, [tenantInvoiceLogo]);

  useEffect(() => {
    if (invoiceData && invoiceData.tenantId) {
      fetchTenantInvoiceLogo(invoiceData.tenantId);
    }
  }, [invoiceData]);

  const handleAfterPrint = React.useCallback(() => {
    console.log("`onAfterPrint` called");
  }, []);

  const handleBeforePrint = React.useCallback(() => {
    console.log("`onBeforePrint` called");
    return Promise.resolve();
  }, []);

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `ProformaInvoice_${proformaInvoice?.invoiceNo || ""}`,
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const downloadPdf = async () => {
    const element = document.getElementById("proforma_invoice_template");
    const buttonContainer = document.getElementById("proformaPrintButtonContainer");

    if (!buttonContainer) {
      console.error("Button container not found");
      return;
    }

    buttonContainer.style.display = "none";

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`ProformaInvoice_${proformaInvoice?.invoiceNo || "Details"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      buttonContainer.style.display = "block";
    }
  };

  return (
    <>
      <style>
        {`
  /* -------- GLOBAL RESET FOR PRINT ---------- */
  @media print {
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    @page {
      size: A4 portrait;
      margin: 8mm !important;
    }

    #proforma_invoice_template {
      width: 192mm !important;
      max-width: 192mm !important;
      margin: 0 auto !important;
      padding: 4mm !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
    }

    #proforma_invoice_template * {
      font-size: 17px !important;
      color: #000 !important;
    }

    table, th, td {
      border: 1px solid #000 !important;
      border-collapse: collapse !important;
    }

    th {
      background: #eaeaea !important;
    }

    #proformaPrintButtonContainer {
      display: none !important;
    }

    img {
      max-width: 150px !important;
      object-fit: contain !important;
    }
  }

  /* -------- SCREEN VIEW ---------- */
  #proforma_invoice_template {
    border: 1px solid #000;
    background: #fff;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
  }

  #proforma_invoice_template * {
    font-size: 18px;
  }

  table, th, td {
    border: 1px solid #000;
    border-collapse: collapse;
  }
`}
      </style>

      <Box id="print-wrapper">
        <Box
          id="proforma_invoice_template"
          ref={componentRef}
          sx={{
            border: "1px solid #000",
            background: "#fff",
            padding: 2,
            width: "100%",
            margin: "0 auto",
            fontFamily: "Arial",
            "& *": {
              fontSize: "16px !important",
            },
          }}
        >
          {/* ---------------- COMPANY HEADER ---------------- */}
          <Grid container spacing={1} alignItems="flex-start">
            <Grid container alignItems="flex-start" sx={{ mb: 1 }}>
              {/* LEFT — COMPANY DETAILS */}
              <Grid item xs={5}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.6rem !important",
                    }}
                  >
                    {proformaInvoice?.tenantName}
                  </Typography>
                  <Typography variant="body2">{proformaInvoice?.msoAddress1}</Typography>
                  <Typography variant="body2">{proformaInvoice?.msoAddress2}</Typography>
                  <Typography variant="body2">{proformaInvoice?.msoAddress3}</Typography>
                  {proformaInvoice?.msoGst && (
                    <Typography variant="body2">GSTIN: {proformaInvoice?.msoGst}</Typography>
                  )}
                </Box>
              </Grid>

              {/* CENTER — LOGO */}
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                {logoSrc && (
                  <img
                    src={logoSrc}
                    alt="Logo"
                    style={{
                      maxWidth: "150px",
                      height: "auto",
                    }}
                  />
                )}
              </Grid>

              {/* RIGHT — PROFORMA INVOICE TITLE */}
              <Grid item xs={5}>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      fontSize: "1.6rem !important",
                      fontWeight: "bold",
                    }}
                  >
                    PROFORMA INVOICE
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic", color: "#555" }}>
                    (This is not a final tax invoice)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Invoice #: {proformaInvoice?.invoiceNo}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* ---------------- META DETAILS TABLE ---------------- */}
          <Table
            size="small"
            sx={{
              mt: 2,
              border: "1px solid #000",
              "& td": { border: "1px solid #000", padding: "4px" },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell>Invoice Date</TableCell>
                <TableCell>{proformaInvoice?.renewDate}</TableCell>
                <TableCell>Mobile No</TableCell>
                <TableCell>{proformaInvoice?.mobileNo}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Plan Name</TableCell>
                <TableCell>{proformaInvoice?.planName}</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>{proformaInvoice?.rateFullName}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Invoice Status</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Invoice Amount</TableCell>
                <TableCell>
                  ₹ {parseFloat(proformaInvoice?.finalInvoiceAmount || 0).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ---------------- BILL TO & SHIP TO ---------------- */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Bill To */}
            <Grid item xs={6}>
              <Box sx={{ border: "1px solid #000" }}>
                <Typography
                  sx={{
                    background: "#e6e6e6",
                    padding: "4px",
                    borderBottom: "1px solid #000",
                    fontWeight: "bold",
                  }}
                >
                  Bill To:
                </Typography>

                <Box sx={{ padding: "6px" }}>
                  <Typography sx={{ fontWeight: "bold" }}>{proformaInvoice?.fullName}</Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Profile Id: {proformaInvoice?.userName}
                  </Typography>
                  <Typography variant="body2">{proformaInvoice?.address}</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Ship To */}
            <Grid item xs={6}>
              <Box sx={{ border: "1px solid #000" }}>
                <Typography
                  sx={{
                    background: "#e6e6e6",
                    padding: "4px",
                    borderBottom: "1px solid #000",
                    fontWeight: "bold",
                  }}
                >
                  Ship To:
                </Typography>

                <Box sx={{ padding: "6px" }}>
                  <Typography sx={{ fontWeight: "bold" }}>{proformaInvoice?.fullName}</Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Profile Id: {proformaInvoice?.userName}
                  </Typography>
                  <Typography variant="body2">{proformaInvoice?.address}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* ---------------- ITEMS TABLE ---------------- */}
          <Table
            size="small"
            sx={{
              mt: 3,
              border: "1px solid #000",
              "& th, & td": { border: "1px solid #000", padding: "6px" },
            }}
          >
            <TableHead>
              <TableRow sx={{ background: "#f0f0f0" }}>
                <TableCell>Item &amp; Description</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell align="center" colSpan={2}>
                  CGST
                </TableCell>
                <TableCell align="center" colSpan={2}>
                  SGST
                </TableCell>
                <TableCell align="center" colSpan={2}>
                  IGST
                </TableCell>
                <TableCell>Total Amount</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell>{proformaInvoice?.planName}</TableCell>
                <TableCell>{parseFloat(proformaInvoice?.planAmount || 0).toFixed(2)}</TableCell>
                <TableCell>{proformaInvoice?.cgstValue || 0}%</TableCell>
                <TableCell>{parseFloat(proformaInvoice?.cgstAmount || 0).toFixed(2)}</TableCell>
                <TableCell>{proformaInvoice?.sgstValue || 0}%</TableCell>
                <TableCell>{parseFloat(proformaInvoice?.sgstAmount || 0).toFixed(2)}</TableCell>
                <TableCell>{proformaInvoice?.igstValue || 0}%</TableCell>
                <TableCell>{parseFloat(proformaInvoice?.igstAmount || 0).toFixed(2)}</TableCell>
                <TableCell>
                  ₹ {parseFloat(proformaInvoice?.finalInvoiceAmount || 0).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ---------------- TOTAL SECTION ---------------- */}
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Grid item xs={6}>
              {/* AMOUNT IN WORDS */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: "bold" }}>Total In Words</Typography>
                <Typography>{proformaInvoice?.finalInvoiceAmountToWords || ""}</Typography>
              </Box>

              {/* BANK DETAILS */}
              {proformaInvoice?.bankAccountNumber && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: "bold" }}>Bank Details</Typography>
                  <Typography>Account Name: {proformaInvoice?.bankAccountName}</Typography>
                  <Typography>Account Number: {proformaInvoice?.bankAccountNumber}</Typography>
                  <Typography>IFSC: {proformaInvoice?.bankIfscCode}</Typography>
                  <Typography>Branch: {proformaInvoice?.bankBranch}</Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={6}>
              <Table
                size="small"
                sx={{
                  border: "1px solid #000",
                  "& td": { border: "1px solid #000", padding: "4px" },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>Sub Total</TableCell>
                    <TableCell align="right">
                      ₹ {parseFloat(proformaInvoice?.planAmount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>

                  {(proformaInvoice?.cgstAmount > 0 || proformaInvoice?.cgstValue > 0) && (
                    <TableRow>
                      <TableCell>CGST ({proformaInvoice?.cgstValue || 0}%)</TableCell>
                      <TableCell align="right">
                        ₹ {parseFloat(proformaInvoice?.cgstAmount || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}

                  {(proformaInvoice?.sgstAmount > 0 || proformaInvoice?.sgstValue > 0) && (
                    <TableRow>
                      <TableCell>SGST ({proformaInvoice?.sgstValue || 0}%)</TableCell>
                      <TableCell align="right">
                        ₹ {parseFloat(proformaInvoice?.sgstAmount || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}

                  {(proformaInvoice?.igstAmount > 0 || proformaInvoice?.igstValue > 0) && (
                    <TableRow>
                      <TableCell>IGST ({proformaInvoice?.igstValue || 0}%)</TableCell>
                      <TableCell align="right">
                        ₹ {parseFloat(proformaInvoice?.igstAmount || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      ₹ {parseFloat(proformaInvoice?.finalInvoiceAmount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>

          {/* ---------------- FOOTER ---------------- */}
          <Typography
            sx={{
              mt: 4,
              textAlign: "center",
              fontSize: "1.0rem",
              color: "#555",
            }}
          >
            ** This is a computer generated proforma invoice, no signature required **
          </Typography>

          <PrintButtonContainer id="proformaPrintButtonContainer">
            <Grid container justifyContent="flex-end" spacing={2} sx={{ marginTop: 2 }}>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={printFn}
                >
                  Print Invoice
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DownloadIcon />}
                  onClick={downloadPdf}
                >
                  Download PDF
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => unSetDialog("proforma_invoice_view")}
                >
                  CLOSE
                </Button>
              </Grid>
            </Grid>
          </PrintButtonContainer>
        </Box>
      </Box>
    </>
  );
}

export default ProformaInvoicePage;
